import React from 'react'
import { X, ExternalLink, Calendar, MapPin, DollarSign, Clock } from 'lucide-react'
import { format } from 'date-fns'

function ConferenceModal({ conference, organisation, onClose, onReportIssue }) {
  if (!conference) return null

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD'
    try {
      return format(new Date(dateString), 'MMM d, yyyy')
    } catch {
      return dateString
    }
  }

  const formatDateRange = (dates) => {
    if (!dates || dates.length === 0) return 'TBD'
    
    if (dates.length === 1) {
      return formatDate(dates[0])
    }
    
    const start = formatDate(dates[0])
    const end = formatDate(dates[dates.length - 1])
    
    if (start === end) return start
    return `${start} - ${end}`
  }

  const formatCost = (cost) => {
    if (!cost) return 'TBD'
    
    if (typeof cost === 'object') {
      const entries = Object.entries(cost)
      if (entries.length === 0) return 'TBD'
      
      return (
        <div>
          {entries.map(([tier, price], index) => (
            <div key={index}>
              <strong>{tier}:</strong> {price}
            </div>
          ))}
        </div>
      )
    }
    
    return cost
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal conference-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{conference.name || 'Unnamed Conference'}</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        
        <div className="modal-content">
          {conference.theme && (
            <div className="field-group">
              <label>Theme</label>
              <p>{conference.theme}</p>
            </div>
          )}
          
          <div className="field-row">
            <div className="field-group">
              <label>Organisation</label>
              <div className="org-info">
                <span>{conference.organisationName}</span>
                {organisation?.website && (
                  <a
                    href={organisation.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="external-link"
                    title="Visit organisation website"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
              {organisation?.type && (
                <div className="org-type">
                  {organisation.type.replace('_', ' ')}
                </div>
              )}
            </div>
            
            <div className="field-group">
              <label>Year</label>
              <p>{conference.year}</p>
            </div>
          </div>

          <div className="field-row">
            <div className="field-group">
              <label><Calendar size={16} /> Dates</label>
              <p>{formatDateRange(conference.dates)}</p>
            </div>
            
            <div className="field-group">
              <label><MapPin size={16} /> Location</label>
              <p>{conference.location || 'TBD'}</p>
              {conference.venue && (
                <div className="venue">{conference.venue}</div>
              )}
            </div>
          </div>

          <div className="field-row">
            <div className="field-group">
              <label><DollarSign size={16} /> Cost</label>
              <div>{formatCost(conference.registrationCost)}</div>
            </div>
            
            <div className="field-group">
              <label><Clock size={16} /> Registration Deadline</label>
              <p>{formatDate(conference.registrationDeadline)}</p>
            </div>
          </div>

          <div className="field-row">
            <div className="field-group">
              <label><Clock size={16} /> Submission Deadline</label>
              <p>{formatDate(conference.paperSubmissionDeadline)}</p>
            </div>
            
            <div className="field-group">
              <label>Confidence</label>
              <div className="confidence-info">
                <span className={`confidence-badge ${
                  conference.confidence >= 0.7 ? 'confidence-high' :
                  conference.confidence >= 0.4 ? 'confidence-medium' : 'confidence-low'
                }`}>
                  {conference.confidence >= 0.7 ? 'High' :
                   conference.confidence >= 0.4 ? 'Medium' : 'Low'}
                </span>
                {!conference.verified && (
                  <span className="unverified-badge">Unverified</span>
                )}
              </div>
            </div>
          </div>

          {conference.relevancePercentage && (
            <div className="field-group">
              <label>Relevance</label>
              <div className="relevance-info">
                <span className="relevance-percentage" style={{
                  backgroundColor: conference.relevancePercentage >= 80 ? '#dcfce7' : 
                                 conference.relevancePercentage >= 60 ? '#fef3c7' : '#fee2e2',
                  color: conference.relevancePercentage >= 80 ? '#166534' : 
                        conference.relevancePercentage >= 60 ? '#92400e' : '#991b1b'
                }}>
                  {conference.relevancePercentage}%
                </span>
                {conference.relevanceReason && (
                  <p className="relevance-reason">{conference.relevanceReason}</p>
                )}
              </div>
            </div>
          )}

          {conference.actualOrganiser && conference.actualOrganiser !== conference.organisationName && (
            <div className="field-group">
              <label>Actually Organised By</label>
              <p className="actual-organiser">{conference.actualOrganiser}</p>
            </div>
          )}

          {conference.conferenceUrl && (
            <div className="field-group">
              <label>Website</label>
              <a
                href={conference.conferenceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="conference-link"
              >
                {conference.conferenceUrl} <ExternalLink size={16} />
              </a>
            </div>
          )}

          {conference.description && (
            <div className="field-group">
              <label>Description</label>
              <p>{conference.description}</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onReportIssue} className="btn btn-secondary">
            Report Issue or Provide Feedback
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConferenceModal